import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';

import { I18nService, TranslatePipe } from '@recruitee/i18n';
import {
  CandidateProfileMoreHotspot,
  IntegrationKinds,
  Partner,
} from '@recruitee/integrations-types';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  NotificationData,
  NotificationsController,
  RtStatefulComponent,
  WindowRef,
} from '@recruitee/user-interface';

import {
  Account,
  ContextualIntegrationsApiService,
  CurrentHotspotToken,
  IntegrationKindToken,
  PartnerToken,
} from '../../client/integrations-client.module';
import {
  CoreApiService,
  Destroyable,
  HttpErrorHandlerService,
  takeUntilDestroyed,
} from '../../common';
import { IntegrationEventActions } from '../../common/events/events';
import { ErrorMessagesWrapperComponent } from '../../misc/error-messages-wrapper/error-messages-wrapper.component';
import { CandidateEmailInput } from '../inputs/candidate-email-input/candidate-email-input.component';
import { DynamicForm } from '../inputs/dynamic-form/dynamic-form';
import { DynamicFormComponent } from '../inputs/dynamic-form/dynamic-form.component';
import {
  Candidate,
  DynamicFormConfig,
  DynamicInputConfigType,
  findInputConfigIndex,
} from '../types';

type State = {
  title: string;
  subtitle: string | null;
  form: DynamicForm;
  candidate: Candidate;
  formLoading: boolean;
  saving: boolean;
  errors: string[];
};

type DialogData = {
  title: string;
  subtitle: string | null;
  formConfig: DynamicFormConfig;
  candidate: Candidate;
};

@Component({
  selector: 'rti-send-candidate-dialog',
  templateUrl: './send-candidate-dialog.component.html',
  styleUrls: ['./send-candidate-dialog.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    ButtonLoadingDirective,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    DynamicFormComponent,
    ErrorMessagesWrapperComponent,
    TranslatePipe,
  ],
})
@Destroyable()
export class SendCandidateDialogComponent
  extends RtStatefulComponent<State>
  implements DialogComponentI<DialogData, boolean>
{
  constructor(
    private dialog: DialogRef,
    private coreApi: CoreApiService,
    private api: ContextualIntegrationsApiService,
    private notificationsCtrl: NotificationsController,
    private i18n: I18nService,
    private httpErrorHandlerService: HttpErrorHandlerService,
    private store: Store,
    private window: WindowRef,
    private account: Account,
    cdRef: ChangeDetectorRef,
    @Inject(CurrentHotspotToken) private currentHotspot: CandidateProfileMoreHotspot,
    @Inject(IntegrationKindToken) private integrationKind: IntegrationKinds,
    @Inject(PartnerToken) private partner?: Partner,
  ) {
    super(cdRef);

    this.createState({
      title: null!,
      subtitle: null!,
      form: null!,
      candidate: null!,
      formLoading: false,
      saving: false,
      errors: [],
    });
  }

  public setDialogData(data: DialogData): void {
    this.validateFormConfigCohesion(data.formConfig);

    this.setState(
      {
        ...this.state,
        title: data.title,
        subtitle: data.subtitle,
        candidate: data.candidate,
      },
      false,
    );

    this.initializeForm(data.formConfig);
  }

  public close(): void {
    this.dialog.close();
  }

  public send(): void {
    this.maybeSetCandidateEmail()
      .pipe(switchMap(() => this.sendCandidate(this.formValue)))
      .subscribe();
  }

  private initializeForm(config: DynamicFormConfig): void {
    const form = this.buildDynamicForm(config);

    form.valueChanges.pipe(takeUntilDestroyed(this)).subscribe(() => this.cdRef.detectChanges());

    form.loading$.pipe(takeUntilDestroyed(this)).subscribe(loading => {
      this.setState({ ...this.state, formLoading: loading });
    });
    this.setState({ ...this.state, form });
  }

  private buildDynamicForm(config: DynamicFormConfig): DynamicForm {
    let result = config;

    const placementsInputConfigIndex = findInputConfigIndex(
      DynamicInputConfigType.CANDIDATE_PLACEMENT,
      config,
    );
    if (placementsInputConfigIndex >= 0) {
      result = Object.assign([], result, {
        [placementsInputConfigIndex]: {
          ...result[placementsInputConfigIndex],
          placements: this.state.candidate.placements,
        },
      });
    }

    const adminsInputConfigIndex = findInputConfigIndex(DynamicInputConfigType.ADMINS, config);
    if (adminsInputConfigIndex >= 0) {
      result = Object.assign([], result, {
        [adminsInputConfigIndex]: {
          ...result[adminsInputConfigIndex],
          value: [this.account.admin],
        },
      });
    }

    return new DynamicForm(result);
  }

  private validateFormConfigCohesion(formConfig: DynamicFormConfig): void {
    if (formConfig.filter(i => i.type === DynamicInputConfigType.CANDIDATE_EMAIL).length > 1)
      throw new Error(`Form has more than 1 ${DynamicInputConfigType.CANDIDATE_EMAIL} inputs`);
  }

  private maybeSetCandidateEmail(): Observable<any> {
    const input = this.candidateEmailInput;

    if (input && !input.config.emails.length) {
      this.setState({ ...this.state, saving: true, errors: [] });

      return this.setCandidateEmail(input.control.value).pipe(
        tap(() => {
          this.setState({ ...this.state, saving: false });
        }),
        catchError(error => {
          this.setState({
            ...this.state,
            saving: false,
            errors: error.error ? error.error.error || [] : [],
          });
          return EMPTY;
        }),
      );
    } else {
      return of(null);
    }
  }

  private get candidateEmailInput(): CandidateEmailInput | null {
    return <CandidateEmailInput>(
      this.state.form.inputs.find(i => i.config.type === DynamicInputConfigType.CANDIDATE_EMAIL)
    );
  }

  private setCandidateEmail(email: string): Observable<any> {
    return this.coreApi
      .patch(`candidates/${this.state.candidate.id}`, {
        candidate: { emails: [email] },
      })
      .pipe(takeUntilDestroyed(this));
  }

  private get formValue(): {} {
    return this.state.form.value;
  }

  private get partnerName(): string {
    if (this.partner) {
      return this.partner.name;
    } else {
      return this.i18n.translate(`rti.${this.integrationKind}.name`);
    }
  }

  // eslint-disable-next-line max-lines-per-function
  private sendCandidate(sendCandidateForm: any): Observable<any> {
    const params = Object.keys(sendCandidateForm).reduce(
      (acc, key) => {
        const value = sendCandidateForm[key];
        if (value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      },
      <any>{},
    );

    this.setState({ ...this.state, saving: true });

    return this.api.post(`candidates/${this.state.candidate.id}/send-candidate`, params).pipe(
      tap(
        (result: { url: string | null; form: DynamicFormConfig | undefined }) => {
          this.store.dispatch(
            IntegrationEventActions.CandidateSentToPartnerEvent({
              payload: {
                context: {
                  hotspot: this.currentHotspot,
                  integrationKind: this.integrationKind,
                  partner: this.partner,
                },
              },
            }),
          );
          const notificationData: NotificationData = {
            title: this.i18n.translate('rti.dynamic.send_candidate.toast.sent', {
              partnerName: this.partnerName,
            }),
          };

          if (result.url)
            notificationData['topButtonLabel'] = this.i18n.translate('rti.common.view');

          const sendingSuccessNotification = this.notificationsCtrl.success(notificationData);

          sendingSuccessNotification
            .topButtonClick()
            .asObservable()
            .pipe(takeUntil(sendingSuccessNotification.beforeClose))
            .subscribe(() => {
              if (result.url) this.window.open(result.url);
            });

          this.close();
        },
        errorResponse => {
          this.setState({ ...this.state, saving: false });

          if (errorResponse.status === 409) {
            const msg = this.i18n.translate(
              `rti.${this.integrationKind}.send_candidate.already_sent`,
            );

            if (msg.startsWith('missing translation')) {
              this.httpErrorHandlerService.default(errorResponse);
            } else {
              this.notificationsCtrl.success(msg);
            }
          } else if (errorResponse.status === 422 && errorResponse.error?.error?.length) {
            this.setState({ ...this.state, errors: errorResponse.error.error });
          } else {
            this.httpErrorHandlerService.default(errorResponse);
          }
        },
      ),
    );
  }
}
